<template>
  <div
    class="pa-0"
    style="
      background-color: #f5f5f5 !important;
      border-left: 1px solid rgba(0, 0, 0, 0.12);
    "
  >
    <v-alert tile id="alert" class="alert">
      <template v-slot:title>
        <v-row no-gutters align="center">
          <v-col>
            <div class="alertHeader mt-3 mb-3 text-left">
              Explore with prompts
            </div>
          </v-col>
          <v-col cols="auto">
            <v-btn
              icon="mdi-close"
              size="small"
              variant="text"
              color="#4D4D4D"
              @click="closeAIPanel"
            />
          </v-col>
        </v-row>
      </template>

      <template v-slot:text>
        <div class="pb-4">
          Expertly curated for your sector, these prompts unlock the most
          relevant insights for your area. Built with care to ensure accuracy
          and tailored to your needs, they save you time by guiding analysis and
          suggesting actions. We still recommend reviewing outputs before
          applying them.
        </div>
      </template>
    </v-alert>
    <div
      class="ma-0 pa-0"
      :style="{
        height:
          parseFloat(displayProps.height) - parseFloat(alertHeight + 60) + 'px',
        overflowY: 'auto',
        overflowX: 'hidden',
      }"
    >
      <div class="pa-2">
        <!-- Skeleton loaders while loading titles -->
        <div v-if="loadingTitles" class="ai-summary-skeletons">
          <v-skeleton-loader
            v-for="n in 3"
            :key="n"
            type="list-item-two-line"
            class="ai-summary-skeleton mb-3"
          ></v-skeleton-loader>
        </div>

        <!-- Actual expansion panels -->
        <v-expansion-panels
          ref="expansionPanels"
          v-else
          variant="accordion"
          class="ai-summary-panels"
          hide-actions
          elevation="1"
          @update:model-value="onPanelToggle"
        >
          <v-expansion-panel
            v-for="aiSummaryTitle in aiSummaryTitles"
            :key="aiSummaryTitle.id"
            :value="aiSummaryTitle.id"
            @click="fetchSummary(aiSummaryTitle.id)"
            elevation="0"
            :class="{
              'ai-panel-open': isPanelOpen(aiSummaryTitle.id),
              'ai-panel-closed': !isPanelOpen(aiSummaryTitle.id),
            }"
            :rounded="true"
          >
            <v-expansion-panel-title class="ai-summary-panel-title">
              <template v-slot:default>
                <div class="d-flex align-center">
                  <v-icon
                    icon="mdi-creation"
                    style="color: #f7b348"
                    class="mr-3"
                    size="small"
                  />
                  <span class="ai-summary-title-text">{{
                    aiSummaryTitle.title
                  }}</span>
                </div>
              </template>
            </v-expansion-panel-title>
            <v-divider></v-divider>
            <v-expansion-panel-text>
              <div class="ai-summary-content">
                <div
                  v-if="loadingSummaries[aiSummaryTitle.id]"
                  class="d-flex align-center justify-center pa-3"
                >
                  <v-progress-circular
                    indeterminate
                    size="24"
                    width="2"
                    class="mr-2"
                  />
                  <span>Generating summary...</span>
                </div>
                <span v-else-if="getSummaryFromStore(aiSummaryTitle.id)">
                  <div
                    v-html="getSummaryFromStore(aiSummaryTitle.id)?.summary"
                    class="ai-summary pa-4"
                  ></div>
                  <v-divider></v-divider>
                  <div
                    class="pa-4 pt-1 pb-1 d-flex align-center justify-space-between"
                  >
                    <!-- Left side: Information icon with text -->
                    <div class="d-flex align-center">
                      <v-icon
                        icon="mdi-information"
                        size="small"
                        color="#7F7F7F"
                        class="mr-2"
                      ></v-icon>
                      <span class="text-caption text-grey font-italic"
                        >Content quality may vary</span
                      >
                    </div>

                    <!-- Right side: Action buttons -->
                    <div class="d-flex align-center" style="gap: 2px">
                      <v-tooltip location="top">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="props"
                            :icon="
                              selectedVote?.[aiSummaryTitle.id] === 'up'
                                ? 'mdi-thumb-up'
                                : 'mdi-thumb-up-outline'
                            "
                            size="small"
                            variant="text"
                            :color="
                              selectedVote?.[aiSummaryTitle.id] === 'up'
                                ? 'success'
                                : '#4D4D4D'
                            "
                            @click="onThumbsClick(aiSummaryTitle.id, 'up')"
                          ></v-btn>
                        </template>
                        <span>Report this summary as helpful</span>
                      </v-tooltip>
                      <v-tooltip location="top">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="props"
                            :icon="
                              selectedVote?.[aiSummaryTitle.id] === 'down'
                                ? 'mdi-thumb-down'
                                : 'mdi-thumb-down-outline'
                            "
                            size="small"
                            variant="text"
                            :color="
                              selectedVote?.[aiSummaryTitle.id] === 'down'
                                ? 'error'
                                : '#4D4D4D'
                            "
                            @click="onThumbsClick(aiSummaryTitle.id, 'down')"
                          ></v-btn>
                        </template>
                        <span>Report this summary as unhelpful</span>
                      </v-tooltip>
                      <v-tooltip location="top">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="props"
                            icon="mdi-content-copy"
                            size="small"
                            variant="text"
                            color="#4D4D4D"
                            @click="copyIndicatorText()"
                          ></v-btn>
                        </template>
                        <span>Copy this summary to your clipboard</span>
                      </v-tooltip>
                    </div>
                  </div>
                </span>
                <div
                  v-else
                  class="d-flex align-center justify-center pa-3"
                  style="cursor: pointer"
                >
                  <span class="text-grey">Click to generate AI summary</span>
                </div>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
        <!-- some breathing room at the bottom -->
        <div style="min-height: 55px !important"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { copyToClipboard } from "@/mixins/CopyToClipboard";
export default {
  name: "NAME",
  data() {
    return {
      alertHeight: 0,
      var1: "val1",
      var2: "val2",
      aiSummaryTitles: null,
      loadingSummaries: {},
      loadingTitles: false,
      openPanelId: null,
      object: {},
      selectedAIPromptID: null,
      selectedVote: {},
    };
  },
  components: {
    // FormsInputsAndControls
  },
  computed: {
    aiReportSummaryContent: {
      get() {
        return this.$store.state.aiReportSummaryContent;
      },
      set(value) {
        this.$store.commit("setAIReportSummaryContent", value);
      },
    },
  },
  props: {
    AIPanel: {
      type: Boolean,
      required: false,
      default: false,
    },
    reportLoadingDialog: {
      type: Boolean,
      required: false,
      default: false,
    },
    displayProps: {
      type: Object,
      required: false,
      default: () => {},
    },
    comparatorsList: {
      required: true,
    },
    indicatorValues: {
      type: Object,
      default: () => ({}),
    },
    reportType: {
      type: [String, null],
      default: "",
    },
    reportTitle: {
      type: [String, null],
      default: "",
    },
    reportObject: {
      type: [Object, null],
      default: () => ({}),
    },
    reportThemes: {
      type: Array,
      default: () => [],
    },
  },
  mounted() {
    this.updateAlertHeight();
    window.addEventListener("resize", this.updateAlertHeight);
    this.getAISummaryTitles();
  },
  methods: {
    closeAIPanel() {
      this.$emit("update:AIPanel", false);
    },
    stripHtmlKeepFormatting(html) {
      if (!html) return "";

      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;

      // Function to process each node
      const processNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent;
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName.toLowerCase();
          const content = Array.from(node.childNodes)
            .map((child) => processNode(child))
            .join("");

          // Preserve formatting by converting HTML tags to text equivalents
          switch (tagName) {
            case "p":
              return content + "\n\n";
            case "br":
              return "\n";
            case "strong":
            case "b":
              return `**${content}**`;
            case "em":
            case "i":
              return `*${content}*`;
            case "ul":
              return content + "\n";
            case "ol":
              return content + "\n";
            case "li":
              return `• ${content}\n`;
            case "h1":
              return `# ${content}\n\n`;
            case "h2":
              return `## ${content}\n\n`;
            case "h3":
              return `### ${content}\n\n`;
            case "h4":
              return `#### ${content}\n\n`;
            case "h5":
              return `##### ${content}\n\n`;
            case "h6":
              return `###### ${content}\n\n`;
            case "blockquote":
              return `> ${content}\n\n`;
            case "code":
              return `\`${content}\``;
            case "pre":
              return `\`\`\`\n${content}\n\`\`\`\n\n`;
            default:
              return content;
          }
        }

        return "";
      };

      return processNode(tempDiv).trim();
    },
    copyIndicatorText() {
      const summary = this.getSummaryFromStore(this.openPanelId).summary;
      const cleanText = this.stripHtmlKeepFormatting(summary);
      copyToClipboard(cleanText);

      //trigger analytics event in backend
      const payload = { event: "reports_ai_output_copied" };
      this.$axios.put("/send-analytics", payload);
    },
    onThumbsClick(promptId, direction) {
      this.selectedVote[promptId] = direction;
      this.sendAnalyticsEvent(direction);
    },
    isPanelOpen(id) {
      // Check if the panel with the given id is currently open
      const isOpen = this.openPanelId === id;
      return isOpen;
    },
    onPanelToggle(value) {
      // Track which panel is currently open
      this.openPanelId = value;
    },
    getSummaryFromStore(id) {
      // aiReportSummaryContent is an array of objects, find the one with matching summary_id
      if (Array.isArray(this.aiReportSummaryContent)) {
        return this.aiReportSummaryContent.find(
          (item) => item.summary_id == id,
        );
      }
      return null;
    },
    addSummaryToStore(id, summaryData) {
      // Ensure aiReportSummaryContent is an array
      let summaries = Array.isArray(this.aiReportSummaryContent)
        ? [...this.aiReportSummaryContent]
        : [];

      // Check if summary already exists and update it, or add new one
      const existingIndex = summaries.findIndex(
        (item) => item.summary_id == id,
      );
      const newSummary = {
        summary_id: parseInt(id),
        summary: summaryData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      if (existingIndex >= 0) {
        summaries[existingIndex] = newSummary;
      } else {
        summaries.push(newSummary);
      }

      this.aiReportSummaryContent = summaries;
    },
    getAISummaryTitles() {
      this.loadingTitles = true;
      this.$axios
        .get("/ai-report-summaries")
        .then(
          function (response) {
            // handle success
            this.aiSummaryTitles = response.data;
            this.loadingTitles = false;
          }.bind(this),
        )
        .catch(
          function (error) {
            // handle error
            console.error(error);
            this.loadingTitles = false;
            this.emit.emit("systemMessage", {
              title: "Error! Failed to get AI summary titles",
              message: error.response.data.message,
              timeout: -1,
              colour: "error",
            });
          }.bind(this),
        );
    },
    updateAlertHeight() {
      this.$nextTick(() => {
        const alertElement = document.getElementById("alert");
        if (alertElement) {
          this.alertHeight = alertElement.offsetHeight;
        } else {
          this.alertHeight = 0;
        }
      });
    },
    fetchSummary(id) {
      this.selectedAIPromptID = id;

      // Prevent multiple requests if already loading
      if (this.loadingSummaries[this.selectedAIPromptID]) {
        return;
      }

      // Check if summary already exists in store (array format)
      const existingSummary = this.getSummaryFromStore(this.selectedAIPromptID);
      if (existingSummary) {
        return;
      }

      this.loadingSummaries[this.selectedAIPromptID] = true;

      // Check if this is a saved report
      const savedReportId = this.$store.state.selectedReportDetails?.id;
      if (!savedReportId) {
        this.generateNewSummary(this.selectedAIPromptID);
        // If it is a saved report, get the summary from the server
      } else {
        this.$axios
          .get(
            "/summary-report-ai-summary/" +
              this.selectedAIPromptID +
              "/" +
              savedReportId,
          )
          .then(
            function (response) {
              // handle success
              if (savedReportId) {
                const summaryData =
                  response.data.summary_data?.summary_data ||
                  response.data.summary;
                this.addSummaryToStore(this.selectedAIPromptID, summaryData);
              } else {
                this.addSummaryToStore(
                  this.selectedAIPromptID,
                  response.data.summary,
                );
              }
              this.loadingSummaries[this.selectedAIPromptID] = false;
            }.bind(this),
          )
          .catch(
            function (error) {
              // handle error
              console.error(error);
              this.loadingSummaries[this.selectedAIPromptID] = false;

              // Check if we need to generate a new summary
              if (
                error.response?.status === 404 &&
                error.response?.data?.action_required === "generate_summary"
              ) {
                // Generate a new summary using the AI report summary endpoint
                this.generateNewSummary(this.selectedAIPromptID);
              } else {
                this.emit.emit("systemMessage", {
                  title: "Error! Failed to generate AI summary",
                  message:
                    error.response?.data?.message ||
                    "An error occurred while generating the summary",
                  timeout: -1,
                  colour: "error",
                });
              }
            }.bind(this),
          );
      }
    },
    updateSavedReportAIOutput(id) {
      const summaryData = this.getSummaryFromStore(id);
      if (summaryData) {
        this.$axios.post("/update-saved-report-ai-output", {
          saved_report_id: this.$store.state.selectedReportDetails?.id,
          summary_id: id,
          summary: summaryData.summary,
        });
      }
    },
    generateNewSummary(id) {
      this.loadingSummaries[id] = true;
      // Generate a new summary using the AI report summary endpoint
      const extractedData = this.generateExtractedData();
      this.$axios
        .post("/user-ai-report-summary", {
          report: JSON.stringify(extractedData),
          promptID: id,
        })
        .then((response) => {
          this.loadingSummaries[id] = false;
          if (response.data.status) {
            this.addSummaryToStore(id, response.data.summary);
            // if this report is a saved report, update the saved report ai output
            if (
              response.data.status &&
              this.$store.state.selectedReportDetails?.id
            ) {
              this.updateSavedReportAIOutput(id);
            }
            /** OI-2301 don't show success snackbar */
            // let them know the summary has been generated
            // this.emit.emit("systemMessage", {
            //   title: "AI Summary Generated",
            //   message: "New AI summary has been generated successfully.",
            //   colour: "success",
            //   timeout: 3000,
            // });
          } else {
            this.emit.emit("systemMessage", {
              title: "Error! Failed to generate AI summary",
              message: response.data.message,
              timeout: 5000,
              colour: "error",
            });
          }
        })
        .catch((error) => {
          console.error("Error generating new summary:", error);
          this.loadingSummaries[id] = false;
          this.emit.emit("systemMessage", {
            title: "Error! Failed to generate AI summary",
            message:
              error.response?.data?.message ||
              "An error occurred while generating the summary, please try again.",
            timeout: -1,
            colour: "error",
          });
        });
    },
    generateExtractedData() {
      // Collect unique areas, indicators, and dates
      const areas = []; // ["Area Name 1", "Area Name 2", ...]
      const indicators = []; // ["Indicator Name 1", "Indicator Name 2", ...]
      const dates = []; // ["2019-01-01", "2021-01-01", ...]

      // Use Maps for fast lookups instead of trying to access the arrays directly
      // This avoids O(n) linear search every time we need to find an index
      const areaMap = new Map();
      const indicatorMap = new Map();
      const dateMap = new Map();

      // Create ultra-compact sparse array structure to eliminate null tokens
      const sparseValues = []; // [areaIndex, indicatorIndex, dateIndex, value]

      // Create a mapping of area codes to comparator names
      const areaCodeToName = {};
      this.comparatorsList.forEach((comparator) => {
        areaCodeToName[comparator.area_code] = comparator.area_name;
      });

      // First pass: collect unique areas, indicators, and dates
      Object.keys(this.indicatorValues).forEach((key) => {
        if (key === "progressID") return;

        const keyParts = key.split(".");
        if (keyParts.length >= 4) {
          const areaCode = keyParts[0];
          const date = keyParts[2];
          const indicatorName = keyParts.slice(3).join(".");
          const areaName = areaCodeToName[areaCode] || areaCode;

          // Add to arrays if not already present
          if (!areaMap.has(areaName)) {
            areaMap.set(areaName, areas.length);
            areas.push(areaName);
          }
          if (!indicatorMap.has(indicatorName)) {
            indicatorMap.set(indicatorName, indicators.length);
            indicators.push(indicatorName);
          }
          if (!dateMap.has(date)) {
            dateMap.set(date, dates.length);
            dates.push(date);
          }
        }
      });

      // Sort dates for consistency
      dates.sort();
      dateMap.clear();
      dates.forEach((date, index) => dateMap.set(date, index));

      // Second pass: populate sparse array with only non-null values
      Object.entries(this.indicatorValues).forEach(([key, value]) => {
        if (key === "progressID") return;

        const keyParts = key.split(".");
        if (keyParts.length >= 4) {
          const areaCode = keyParts[0];
          const date = keyParts[2];
          const indicatorName = keyParts.slice(3).join(".");
          const areaName = areaCodeToName[areaCode] || areaCode;

          const areaIdx = areaMap.get(areaName);
          const indicatorIdx = indicatorMap.get(indicatorName);
          const dateIdx = dateMap.get(date);

          if (
            areaIdx !== undefined &&
            indicatorIdx !== undefined &&
            dateIdx !== undefined
          ) {
            // Only store non-null values: [areaIndex, indicatorIndex, dateIndex, value]
            sparseValues.push([areaIdx, indicatorIdx, dateIdx, value]);
          }
        }
      });

      return {
        metadata: {
          title: this.reportTitle,
          area: this.comparatorsList[0]?.area_name,
          type: this.reportType,
        },
        content: {
          intro: this.reportObject?.introduction,
          about_indicators: this.reportObject?.about_ind,
          about_area: this.reportObject?.how_area_identified,
        },
        data: {
          areas,
          indicators,
          dates,
          values: sparseValues,
        },
        themes: this.reportThemes
          .map((theme) => ({
            name: theme.title,
            desc: theme.description,
            sections: theme.config
              .filter((config) => config.text && config.text.trim() !== "")
              .map((config) => config.text),
          }))
          .filter((theme) => theme.sections.length > 0),
      };
    },
    sendAnalyticsEvent(type) {
      //build analytics payload
      let payload = {
        event: "reports_ai_output_feedback",
        feedback: type === "up" ? "helpful" : "unhelpful",
        prompt_id: this.selectedAIPromptID,
        summary_report: this.reportType.toLowerCase() === "summary",
        comparitor_one_name: this.comparatorsList[0]?.area_name || "",
        comparitor_one_area_code: this.comparatorsList[0]?.area_code || "",
        comparitor_two_name: this.comparatorsList[1]?.area_name || "",
        comparitor_two_area_code: this.comparatorsList[1]?.area_code || "",
        comparitor_three_name: this.comparatorsList[2]?.area_name || "",
        comparitor_three_area_code: this.comparatorsList[2]?.area_code || "",
        reportID: this.reportObject?.id || null,
      };
      if (this.reportType.toLowerCase() === "custom") {
        this.reportThemes.forEach((theme) => {
          payload[`report_theme_${theme.id}`] = theme.title;
        });
      }

      //trigger analytics event in backend
      this.$axios.put("/send-analytics", payload);
    },
    test() {
      // console.log('this is a test')
    },
  },
  watch: {
    selectedItem: {
      handler() {
        // this.$emit('selectedItem', this.selectedItem)
      },
      deep: true,
    },
  },
  unmounted() {
    window.removeEventListener("resize", this.updateAlertHeight);
  },
};
</script>

<style scoped>
.alert {
  align-self: stretch;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  padding: 10px;
  background-color: #f5f5f5 !important;
  color: var(--Text-Primary, #4d4d4d);
  /* Caption/Regular */
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.alertHeader {
  color: var(--Text-Primary, #4d4d4d);
  text-align: left;
  font-family: Inter;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.ai-summary-panels {
  background-color: transparent !important;
}

.ai-summary-panels .v-expansion-panel {
  margin-bottom: 10px !important;
}

.ai-summary-panels .v-expansion-panel:last-child {
  margin-bottom: 0 !important;
}

.ai-summary-panel-title {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: var(--Text-Primary, #4d4d4d) !important;
  padding: 12px 16px !important;
}

.ai-summary-title-text {
  color: var(--Text-Primary, #4d4d4d);
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.ai-summary-content {
  padding: 0px !important;
  font-size: 14px;
  line-height: 1.5;
  color: var(--Text-Primary, #4d4d4d);
}

.ai-summary-skeletons {
  margin-bottom: 0;
}

.ai-summary-skeleton {
  border-radius: 4px;
}

.ai-panel-closed {
  border: rgba(0, 0, 0, 0.12) 1px solid;
}

.ai-panel-open {
  padding: 0px !important;
  border-radius: var(--Spacing-XS, 4px);
  border: 2px solid var(--Info-Light, #3e7bad);
  background: var(--Common-White, #fff);

  /* Elevation Dark/1 */
  box-shadow:
    0 1px 3px 1px rgba(0, 0, 0, 0.15),
    0 1px 2px 0 rgba(0, 0, 0, 0.3);
}

/* Remove padding from expansion panel components */
.ai-panel-open .v-expansion-panel-title {
  padding: 12px 16px !important;
}

.ai-panel-open .v-expansion-panel-text {
  padding: 0px !important;
}

.ai-panel-open .v-expansion-panel-text__wrapper {
  padding: 0px !important;
}

.ai-panel-open .ai-summary-content {
  padding: 0px !important;
  margin: 0px !important;
}

/* Target all possible nested elements that might have padding */
.ai-panel-open .v-expansion-panel-text > * {
  padding: 0px !important;
  margin: 0px !important;
}

.ai-panel-open .v-expansion-panel-text__wrapper > * {
  padding: 0px !important;
  margin: 0px !important;
}

.ai-summary {
  color: var(--Text-Primary, #4d4d4d);

  /* Caption/Regular */
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.ai-summary p {
  margin-bottom: 12px !important;
}

.ai-summary p:last-child {
  margin-bottom: 0 !important;
}

/* Alternative approach - target all elements within ai-summary */
.ai-summary > * {
  margin-bottom: 12px;
}

.ai-summary > *:last-child {
  margin-bottom: 0;
}

/* Remove padding from ai-summary when panel is open */
.ai-panel-open .ai-summary {
  margin: 0px !important;
}
</style>
